import {
  DEFAULT_APPEARANCE,
  DEFAULT_FOLDER_TAG_COLOR,
  DEFAULT_SAVE_MODE,
  GRID_SIZE_OPTIONS,
  LEGACY_DEFAULT_FOLDER_TAG_COLOR,
  ROOT_FOLDER_ID,
  SORT_OPTIONS,
  STORAGE_KEY,
  TAG_COLORS,
} from './constants';
import {
  appearanceMatches,
  createDefaultWorkspace,
  getFileDisplayName,
  normalizeName,
} from './workspace';

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function assertImport(condition, message) {
  if (!condition) {
    throw new Error(message);
  }
}

function validateEditorState(editorState, fileName) {
  if (editorState === null || editorState === undefined) {
    return;
  }

  assertImport(typeof editorState === 'string', `${fileName} has invalid editor content.`);

  try {
    const parsed = JSON.parse(editorState);
    assertImport(isPlainObject(parsed?.root), `${fileName} has invalid rich text content.`);
  } catch {
    throw new Error(`${fileName} has invalid rich text content.`);
  }
}

function validateAppearance(appearance, fileName) {
  if (appearance === undefined || appearance === null) {
    return;
  }

  assertImport(isPlainObject(appearance), `${fileName} has invalid appearance settings.`);

  if (appearance.backgroundColor !== undefined) {
    assertImport(
      typeof appearance.backgroundColor === 'string' && !/[<>{};]/.test(appearance.backgroundColor),
      `${fileName} has an invalid background color.`
    );
  }

  if (appearance.textColor !== undefined) {
    assertImport(
      typeof appearance.textColor === 'string' && !/[<>{};]/.test(appearance.textColor),
      `${fileName} has an invalid text color.`
    );
  }

  if (appearance.fontFamily !== undefined) {
    assertImport(
      typeof appearance.fontFamily === 'string' && !/[<>{};]/.test(appearance.fontFamily),
      `${fileName} has an invalid font family.`
    );
  }

  if (appearance.fontSize !== undefined) {
    const fontSize = Number(appearance.fontSize);
    assertImport(
      Number.isFinite(fontSize) && fontSize >= 8 && fontSize <= 96,
      `${fileName} has an invalid font size.`
    );
  }

  if (appearance.lineHeight !== undefined) {
    const lineHeight = Number(appearance.lineHeight);
    assertImport(
      Number.isFinite(lineHeight) && lineHeight >= 0.8 && lineHeight <= 3.5,
      `${fileName} has an invalid line height.`
    );
  }
}

function validateImportedWorkspace(workspaceData) {
  assertImport(isPlainObject(workspaceData), 'Invalid TextLab backup file.');
  assertImport(Array.isArray(workspaceData.folders), 'Backup file is missing folders.');
  assertImport(Array.isArray(workspaceData.files), 'Backup file is missing files.');
  assertImport(workspaceData.folders.length > 0, 'Backup file must include a root folder.');

  const folderIds = new Set();
  const itemNamesByParent = new Map();

  workspaceData.folders.forEach((folder) => {
    assertImport(isPlainObject(folder), 'Backup file includes an invalid folder.');
    assertImport(typeof folder.id === 'string' && folder.id.trim(), 'A folder is missing its id.');
    assertImport(!folderIds.has(folder.id), `Folder id "${folder.id}" is duplicated.`);
    assertImport(
      typeof folder.name === 'string' && folder.name.trim(),
      `Folder "${folder.id}" is missing its name.`
    );
    assertImport(
      folder.parentId === null || typeof folder.parentId === 'string',
      `Folder "${folder.name}" has an invalid parent.`
    );

    folderIds.add(folder.id);
  });

  assertImport(folderIds.has(ROOT_FOLDER_ID), 'Backup file is missing the TextLab root folder.');
  const folderById = new Map(workspaceData.folders.map((folder) => [folder.id, folder]));

  workspaceData.folders.forEach((folder) => {
    if (folder.id === ROOT_FOLDER_ID) {
      assertImport(folder.parentId === null, 'The root folder cannot have a parent.');
      return;
    }

    assertImport(folder.parentId, `Folder "${folder.name}" is missing its parent.`);
    assertImport(folder.parentId !== folder.id, `Folder "${folder.name}" cannot be its own parent.`);
    assertImport(
      folderIds.has(folder.parentId),
      `Folder "${folder.name}" points to a missing parent.`
    );

    let cursor = folder.parentId;
    const visited = new Set([folder.id]);
    while (cursor) {
      assertImport(!visited.has(cursor), `Folder "${folder.name}" creates a folder cycle.`);
      visited.add(cursor);
      const parent = folderById.get(cursor);
      cursor = parent?.parentId || null;
    }

    const parentKey = folder.parentId || ROOT_FOLDER_ID;
    const siblingNames = itemNamesByParent.get(parentKey) || new Set();
    const normalizedName = normalizeName(folder.name);
    assertImport(
      !siblingNames.has(normalizedName),
      `Folder "${folder.name}" duplicates another item in the same folder.`
    );
    siblingNames.add(normalizedName);
    itemNamesByParent.set(parentKey, siblingNames);
  });

  const fileIds = new Set();

  workspaceData.files.forEach((file) => {
    assertImport(isPlainObject(file), 'Backup file includes an invalid file.');
    assertImport(typeof file.id === 'string' && file.id.trim(), 'A file is missing its id.');
    assertImport(!fileIds.has(file.id), `File id "${file.id}" is duplicated.`);
    assertImport(
      typeof file.name === 'string' && file.name.trim(),
      `File "${file.id}" is missing its name.`
    );
    assertImport(
      typeof file.parentFolderId === 'string' && folderIds.has(file.parentFolderId),
      `File "${file.name}" points to a missing folder.`
    );
    assertImport(
      file.content === undefined || typeof file.content === 'string',
      `File "${file.name}" has invalid text content.`
    );

    validateEditorState(file.editorState, file.name);
    validateAppearance(file.appearance, file.name);

    const siblingNames = itemNamesByParent.get(file.parentFolderId) || new Set();
    const normalizedName = normalizeName(
      getFileDisplayName({ ...file, name: file.name.replace(/\.txt$/i, ''), extension: '.txt' })
    );
    assertImport(
      !siblingNames.has(normalizedName),
      `File "${file.name}" duplicates another item in the same folder.`
    );
    siblingNames.add(normalizedName);
    itemNamesByParent.set(file.parentFolderId, siblingNames);
    fileIds.add(file.id);
  });

  if (workspaceData.settings !== undefined) {
    assertImport(isPlainObject(workspaceData.settings), 'Backup file has invalid settings.');
  }
}

export function normalizeWorkspace(workspace) {
  const fallback = createDefaultWorkspace();

  if (!workspace || !Array.isArray(workspace.folders) || !Array.isArray(workspace.files)) {
    return fallback;
  }

  const folders = workspace.folders.length ? workspace.folders : fallback.folders;
  const hasRoot = folders.some((folder) => folder.id === ROOT_FOLDER_ID);
  const savedGridSize = workspace.settings?.gridSize;
  const gridSizeValues = new Set(GRID_SIZE_OPTIONS.map((option) => option.value));
  const normalizedGridSize =
    savedGridSize === 'veryLarge'
      ? 'large'
      : gridSizeValues.has(savedGridSize)
        ? savedGridSize
        : 'small';
  const savedFilter = workspace.settings?.explorerFilter;
  const filterValues = new Set(['all', 'favorites', 'files', ...TAG_COLORS.map((tag) => tag.value)]);
  const explorerFilter = filterValues.has(savedFilter) ? savedFilter : 'all';
  const savedSort = workspace.settings?.explorerSort;
  const sortValues = new Set(SORT_OPTIONS.map((option) => option.value));
  const explorerSort = sortValues.has(savedSort) ? savedSort : 'nameAsc';
  const hasStoredDefaultFolderTagColor =
    typeof workspace.settings?.defaultFolderTagColor === 'string';
  const defaultFolderTagColor =
    workspace.settings?.defaultFolderTagColor || DEFAULT_FOLDER_TAG_COLOR;
  const defaultFileAppearance = {
    ...DEFAULT_APPEARANCE,
    ...(workspace.settings?.defaultFileAppearance || {}),
  };
  const availableTagValues = TAG_COLORS.map((tag) => tag.value).filter(
    (value) => value !== defaultFolderTagColor
  );
  const normalizedExplorerFilter =
    explorerFilter !== 'all' &&
    explorerFilter !== 'favorites' &&
    explorerFilter !== 'files' &&
    !availableTagValues.includes(explorerFilter)
      ? 'all'
      : explorerFilter;

  return {
    version: 1,
    folders: (hasRoot ? folders : fallback.folders).map((folder) => {
      const normalizedTagColor =
        folder.id === ROOT_FOLDER_ID
          ? null
          : !folder.tagColor ||
              (!hasStoredDefaultFolderTagColor &&
                folder.tagColor === LEGACY_DEFAULT_FOLDER_TAG_COLOR)
            ? defaultFolderTagColor
            : folder.tagColor;
      const usesDefaultTag =
        folder.id === ROOT_FOLDER_ID
          ? false
          : typeof folder.usesDefaultTag === 'boolean'
            ? folder.usesDefaultTag
            : normalizedTagColor === defaultFolderTagColor;

      return {
        ...folder,
        tagColor: usesDefaultTag ? defaultFolderTagColor : normalizedTagColor,
        usesDefaultTag,
      };
    }),
    files: workspace.files.map((file) => {
      const normalizedAppearance = { ...DEFAULT_APPEARANCE, ...(file.appearance || {}) };
      const usesDefaultAppearance =
        typeof file.usesDefaultAppearance === 'boolean'
          ? file.usesDefaultAppearance
          : appearanceMatches(defaultFileAppearance, normalizedAppearance);

      return {
        ...file,
        extension: '.txt',
        editorState: typeof file.editorState === 'string' ? file.editorState : null,
        appearance: usesDefaultAppearance ? { ...defaultFileAppearance } : normalizedAppearance,
        usesDefaultAppearance,
        isFavorite: Boolean(file.isFavorite),
      };
    }),
    settings: {
      theme: workspace.settings?.theme === 'dark' ? 'dark' : 'light',
      viewMode: workspace.settings?.viewMode === 'list' ? 'list' : 'grid',
      gridSize: normalizedGridSize,
      explorerFilter: normalizedExplorerFilter,
      explorerSort,
      favoritesOpen: workspace.settings?.favoritesOpen !== false,
      saveMode: workspace.settings?.saveMode === 'immediate' ? 'immediate' : DEFAULT_SAVE_MODE,
      defaultFolderTagColor,
      defaultFileAppearance,
      lastOpenedFolderId: workspace.settings?.lastOpenedFolderId || ROOT_FOLDER_ID,
    },
    updatedAt: workspace.updatedAt || fallback.updatedAt,
  };
}

export function loadWorkspace() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return createDefaultWorkspace();
    }

    return normalizeWorkspace(JSON.parse(raw));
  } catch (error) {
    console.warn('TextLab could not load localStorage data. A fresh workspace was created.', error);
    return createDefaultWorkspace();
  }
}

export function importWorkspaceData(workspaceData) {
  validateImportedWorkspace(workspaceData);
  return normalizeWorkspace(workspaceData);
}

export function saveWorkspace(workspace) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(workspace));
  } catch (error) {
    console.warn('TextLab could not save to localStorage.', error);
  }
}
